import { toSeconds, valveStateOf } from './device';

const SUCCESSFUL_STATUSES = new Set(['executed', 'duplicate', 'duplicate_ignored']);
const REJECTED_STATUSES = new Set(['failed', 'rejected', 'expired', 'unconfirmed']);

/** Capability reported by the device itself, never inferred from its name/version. */
export function supportsValveCommandProtocol(device) {
  return device?.valve_command_protocol_version === 1;
}

/**
 * An unchanged/stale state cannot acknowledge a new command. Protocol 1 must
 * correlate the actual reported command; legacy firmware can only provide a
 * later telemetry observation, which the UI labels separately.
 */
export function evaluateValveCommandReport({ device, command, target, initialDevice, sentAtSeconds }) {
  const commandId = command?.command_id;
  const commandTimestamp = Number(command?.command_timestamp);
  const correlatedAck = Boolean(commandId)
    && device?.last_acknowledged_command_id === commandId
    && Number(device?.last_acknowledged_command_timestamp) === commandTimestamp;

  if (correlatedAck && REJECTED_STATUSES.has(device.last_acknowledged_command_status)) {
    return { status: 'rejected', commandStatus: device.last_acknowledged_command_status };
  }

  if (valveStateOf(device) !== target) return { status: 'pending' };

  if (supportsValveCommandProtocol(initialDevice) || supportsValveCommandProtocol(device)) {
    const correlatedReport = Boolean(commandId)
      && Number.isSafeInteger(commandTimestamp) && commandTimestamp > 0
      && device?.last_reported_command_id === commandId
      && Number(device?.last_reported_command_timestamp) === commandTimestamp;
    if (!correlatedReport) return { status: 'pending' };
    if (correlatedAck && !SUCCESSFUL_STATUSES.has(device.last_acknowledged_command_status)) {
      return { status: 'pending' };
    }
    return { status: 'confirmed', confirmationMode: 'command' };
  }

  const issuedAt = toSeconds(command?.issued_at) || toSeconds(sentAtSeconds);
  const lastSeen = toSeconds(device?.last_seen ?? device?.lastSeen);
  const initialLastSeen = toSeconds(initialDevice?.last_seen ?? initialDevice?.lastSeen);
  const reportedAt = toSeconds(device?.reported_state_updated_at) || lastSeen;
  if (issuedAt > 0 && lastSeen > initialLastSeen && reportedAt > issuedAt) {
    return { status: 'confirmed', confirmationMode: 'legacy_telemetry' };
  }
  return { status: 'pending' };
}
